import { DAY_MS, localToday } from './local-date'

export const SPRINT_LENGTH_DAYS = 14

export type Sprint = {
  name: string
  start: Date
  /** Exclusive: the Monday the next sprint starts. */
  end: Date
}

/** Option names are `Sprint YYYY-MM-DD` (Monday start, 14-day duration). */
export function parseSprintName(name: string): Sprint | null {
  const m = /^Sprint (\d{4}-\d{2}-\d{2})$/.exec(name)
  if (!m) return null
  const start = new Date(m[1] + 'T00:00:00Z')
  if (Number.isNaN(start.getTime())) return null
  return { name, start, end: new Date(start.getTime() + SPRINT_LENGTH_DAYS * DAY_MS) }
}

function parseAll(options: string[]): Sprint[] {
  return options
    .map(parseSprintName)
    .filter((s): s is Sprint => s !== null)
    .sort((a, b) => a.start.getTime() - b.start.getTime())
}

/** The sprint whose [start, end) window contains today, or null between sprints. */
export function currentSprint(options: string[], today: Date = localToday()): Sprint | null {
  return parseAll(options).find((s) => s.start <= today && today < s.end) ?? null
}

export function nextSprint(options: string[], today: Date = localToday()): Sprint | null {
  return parseAll(options).find((s) => s.start > today) ?? null
}

export function previousSprint(options: string[], today: Date = localToday()): Sprint | null {
  const past = parseAll(options).filter((s) => s.end <= today)
  return past[past.length - 1] ?? null
}

/** 1-indexed day within the sprint ("Day 6 of 14"). */
export function sprintDay(sprint: Sprint, today: Date = localToday()): number {
  const day = Math.floor((today.getTime() - sprint.start.getTime()) / DAY_MS) + 1
  return Math.min(Math.max(day, 1), SPRINT_LENGTH_DAYS)
}

/**
 * Velocity = issues whose Released at falls inside the given sprint's window.
 * Computed from the date field, NOT from Stage timeline events (avoids N+1).
 */
export function releasedDuring(sprint: Sprint, releasedAt: string | null): boolean {
  if (!releasedAt) return false
  const released = new Date(releasedAt + (releasedAt.length === 10 ? 'T00:00:00Z' : ''))
  if (Number.isNaN(released.getTime())) return false
  return sprint.start <= released && released < sprint.end
}
